import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useRecoilState, useRecoilValue } from 'recoil';

import { useClinicalNoteContext } from 'lib/context/ClinicalNoteContext/ClinicalNoteContext';
import useLoading from 'lib/hooks/useLoading';

import Icon from 'components/shared/Icon/Icon';
import IntercomButton from 'components/shared/IntercomButton/IntercomButton';
import { clinicalNote as clinicalNoteState, userState as user_state } from 'components/state';

import { showPreviewNote } from '../lib/showPreviewNote';
import ConfirmSignLock from '../shared/ConfirmSignLock';

import ChartLinks from './components/ChartLinks';
import DOSLinks from './components/DOSLinks';
import MyScribeAILink from './components/MyScribeAILink';
import NavbarContextMenu from './components/NavbarContextMenu';
import SaveAndPreview from './components/SaveAndPreview';
import SignAndLock from './components/SignAndLock';
import TreatmentPlan from './components/TreatmentPlan';
import { onShowConfirmSignLock as handleShowConfirmSignLock } from './components/lib/helpers';
import useClinicalNoteSignatures from './components/lib/hooks/useClinicalNoteSignatures';

const Navbar = ({ forwardedRef }) => {
  const navigate = useNavigate();
  const { id: patientId, appointmentId } = useParams();
  const user = useRecoilValue(user_state);

  const [clinicalNote, setClinicalNote] = useRecoilState(clinicalNoteState);
  const {
    cnDisplaySettings,
    setCNDisplaySettings,
    saveButtonText,
    setDocumentSignInfo,
    showConfirmSignLock,
    setAdvancedHP,
    setAdvancedSOAP,
    setCustomFormTypes,
    setIsExport,
    setShowPreviewNoteLoading,
    saveNoteRef = null
  } = useClinicalNoteContext() || {};
  const [autosave, setAutosave] = useState(
    user?.display_settings?.clinicalNote?.auto_save?.enabled
  );
  const { loading, startLoading, stopLoading } = useLoading();

  const { userSign, documentSignatures, userAssignedToDocument } = useClinicalNoteSignatures({
    setDocumentSignInfo,
    saveButtonText
  });

  const onShowConfirmSignLock = async () => {
    await handleShowConfirmSignLock({
      clinicalNote,
      showConfirmSignLock,
      params: { id: patientId, appointmentId },
      stopLoading,
      startLoading,
      saveButtonText,
      setClinicalNote,
      setAdvancedHP,
      setAdvancedSOAP,
      setCustomFormTypes,
      setIsExport,
      saveNoteRef,
      navigate,
      setShowPreviewNoteLoading,
      showPreviewNote
    });
  };

  const handleAutoSave = async (event) => {
    setAutosave(event);
    const updatedSettings = {
      ...cnDisplaySettings,
      auto_save: { ...cnDisplaySettings.auto_save, enabled: event }
    };
    setCNDisplaySettings(updatedSettings);
  };

  return (
    <div
      className="flex items-center gap-2 bg-primary-900 !px-4 py-[10px] pl-3 pr-6 sm:justify-between md:items-center md:justify-between lg:items-center lg:justify-between xl:items-center xl:justify-between"
      ref={forwardedRef}
      data-dd-privacy="allow">
      <div className="item-center flex gap-x-4">
        <div
          className="my-auto flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-primary-800 hover:!bg-primary-700"
          onClick={() => navigate(`/portal/charts/${patientId}/clinical-notes`)}>
          <Icon icon="chevron-left-linear" size={16} color="white" className="cursor-pointer" />
        </div>
        <Icon icon="logo-symbol" color="primary" shade={100} fill stroke />
        <div className="flex items-center gap-x-3">
          <ChartLinks />
          <IntercomButton />
          <MyScribeAILink />
        </div>
      </div>
      <div className="flex items-center gap-x-3 sm:gap-x-2 md:gap-x-2">
        {/* TODO: Search down bellow commented for now */}
        {/* <div className="flex items-center justify-center gap-x-2">
            <Icon icon="search-mono" size={18} color="white" />
            <p className="text-sm font-500 text-white">Search</p>
          </div> */}
        <DOSLinks />
        <TreatmentPlan />
        <SignAndLock />
        <SaveAndPreview autosave={autosave} handleAutoSave={handleAutoSave} />
        <NavbarContextMenu
          loading={loading}
          userSign={userSign}
          autosave={autosave}
          handleAutoSave={handleAutoSave}
          onShowConfirmSignLock={onShowConfirmSignLock}
          userAssignedToDocument={userAssignedToDocument}
        />
        <ConfirmSignLock documentSignatures={documentSignatures} hasUserSigned={userSign} />
      </div>
    </div>
  );
};

export default Navbar;
